import type { AgentConfig, Message, ErrorType } from "../../shared/types";
import {
  EventType,
  type StreamEvent,
  type ToolCallStartEvent,
  type ToolCallEndEvent,
} from "./agui-protocol";

export interface ApiError extends Error {
  errorType: ErrorType;
  statusCode?: number;
  retryable: boolean;
  userMessage: string;
}

interface AgUiEventCallbacks {
  onThreadId: (threadId: string) => void;
  onDelta: (delta: string) => void;
  onError: (message: string) => void;
  onStatus?: (event: ToolCallStartEvent | ToolCallEndEvent) => void;
}

function createApiError(
  errorType: ErrorType,
  message: string,
  statusCode?: number,
  retryable: boolean = true,
): ApiError {
  const error = new Error(message) as ApiError;
  error.errorType = errorType;
  error.statusCode = statusCode;
  error.retryable = retryable;
  error.userMessage = message;
  return error;
}

function getErrorMessage(
  statusCode: number,
  statusText: string,
): {
  errorType: ErrorType;
  message: string;
  retryable: boolean;
} {
  switch (statusCode) {
    case 429:
      return {
        errorType: "rateLimit",
        message:
          "I'm receiving too many requests right now. Please wait a moment and try again.",
        retryable: true,
      };
    case 500:
    case 502:
    case 503:
    case 504:
      return {
        errorType: "serverError",
        message:
          "Something went wrong on my end. Please try again in a moment.",
        retryable: true,
      };
    case 400:
      return {
        errorType: "clientError",
        message: "I couldn't understand that request. Please try rephrasing.",
        retryable: false,
      };
    case 401:
    case 403:
      return {
        errorType: "clientError",
        message: "I don't have permission to process that request.",
        retryable: false,
      };
    default:
      return {
        errorType: "unknown",
        message: "An unexpected error occurred. Please try again.",
        retryable: true,
      };
  }
}

function handleAgUiEvent(
  event: StreamEvent,
  callbacks: AgUiEventCallbacks,
): void {
  switch (event.type) {
    case EventType.RUN_STARTED:
      if ("threadId" in event && event.threadId) {
        callbacks.onThreadId(event.threadId);
      }
      break;

    case EventType.TEXT_MESSAGE_START:
      if ("conversationId" in event && event.conversationId) {
        callbacks.onThreadId(event.conversationId);
      }
      break;

    case EventType.TEXT_MESSAGE_CONTENT:
      if ("delta" in event && event.delta) {
        callbacks.onDelta(event.delta);
      }
      break;

    case EventType.TEXT_MESSAGE_END:
      break;

    case EventType.RUN_FINISHED:
      break;

    case EventType.RUN_ERROR:
      if ("message" in event) {
        callbacks.onError(event.message || "Unknown error occurred");
      }
      break;

    case EventType.TOOL_CALL_START:
    case EventType.TOOL_CALL_END:
      callbacks.onStatus?.(event);
      break;

    case EventType.REASONING_START:
    case EventType.REASONING_END:
      break;

    default:
      if (import.meta.env.DEV) {
        console.warn("Unknown AG-UI event type:", event);
      }
  }
}

export class ApiClient {
  private baseUrl: string;
  private agentId: string;

  constructor(baseUrl: string, agentId: string) {
    this.baseUrl = baseUrl;
    this.agentId = agentId;
  }

  async getAgentConfig(): Promise<AgentConfig> {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/v1/public/agents/${this.agentId}/configuration`,
      );

      if (!response.ok) {
        const { errorType, message, retryable } = getErrorMessage(
          response.status,
          response.statusText,
        );
        throw createApiError(errorType, message, response.status, retryable);
      }

      return response.json();
    } catch (error) {
      // Handle network errors
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw createApiError(
          "network",
          "I'm having trouble connecting right now. Please check your internet connection and try again.",
          undefined,
          true,
        );
      }
      // Re-throw ApiError as-is
      if ((error as ApiError).errorType) {
        throw error;
      }
      // Wrap unknown errors
      throw createApiError(
        "unknown",
        "An unexpected error occurred. Please try again.",
        undefined,
        true,
      );
    }
  }

  async sendMessage(
    message: string,
    conversationId: string | null,
    onChunk?: (chunk: string) => void,
    onStatus?: (event: ToolCallStartEvent | ToolCallEndEvent) => void,
  ): Promise<{
    conversationId: string;
    message: Message;
  }> {
    if (conversationId) {
      console.debug("[widget] sendMessage conversationId:", conversationId);
    }

    // Build request body, only include conversationId if it's a valid GUID format
    const requestBody: { message: string; conversationId?: string } = {
      message,
    };
    const guidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (conversationId && guidRegex.test(conversationId)) {
      requestBody.conversationId = conversationId;
    }

    let response: Response;
    try {
      response = await fetch(
        `${this.baseUrl}/api/v1/public/agents/${this.agentId}/chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );
    } catch (error) {
      // Handle network errors (fetch failures)
      if (error instanceof TypeError) {
        throw createApiError(
          "network",
          "I'm having trouble connecting right now. Please check your internet connection and try again.",
          undefined,
          true,
        );
      }
      // Re-throw ApiError as-is
      if ((error as ApiError).errorType) {
        throw error;
      }
      // Wrap unknown errors
      throw createApiError(
        "network",
        "I'm having trouble connecting right now. Please check your internet connection and try again.",
        undefined,
        true,
      );
    }

    if (!response.ok) {
      const { errorType, message, retryable } = getErrorMessage(
        response.status,
        response.statusText,
      );

      throw createApiError(errorType, message, response.status, retryable);
    }

    if (!response.body) {
      throw new Error("Response body is null");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullContent = "";
    let conversationIdFromStream = conversationId;

    const appendChunk = (text: string) => {
      fullContent += text;
      onChunk?.(text);
    };

    const streamingError = createApiError(
      "serverError",
      "Something went wrong while processing your request. Please try again.",
      500,
      true,
    );

    const eventCallbacks: AgUiEventCallbacks = {
      onThreadId: (threadId: string) => {
        conversationIdFromStream = threadId;
      },
      onDelta: (delta: string) => {
        appendChunk(delta);
      },
      onError: () => {
        throw streamingError;
      },
      onStatus,
    };

    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6).trim();

            if (data === "[DONE]") {
              continue;
            }

            try {
              const event = JSON.parse(data) as StreamEvent;
              handleAgUiEvent(event, eventCallbacks);
            } catch (e) {
              if (e instanceof SyntaxError) {
                console.error("Failed to parse AG-UI event:", data, e);
                throw streamingError;
              } else {
                throw e;
              }
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    const assistantMessage: Message = {
      id: Date.now().toString(),
      role: "assistant",
      content: fullContent,
      timestamp: Date.now(),
    };

    return {
      conversationId: conversationIdFromStream || "",
      message: assistantMessage,
    };
  }
}
